/**
 * 绑定车辆
 */
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, message, Button, Table, Pagination, Row, Col } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import { firmwareBindRequest, firmwareCarsByVersionPrefixRequest } from '@/service/firmwareManage';
import { link } from 'fs';

interface BindModalProps {
  getFirmwareData: () => void;
  closeModal: () => void;
  id: string;
  otaVersionPre: string;
  packet?: number;
}
const BindModal = (props: BindModalProps) => {
  const [form] = Form.useForm();
  const [searchForm] = Form.useForm();
  const { getFirmwareData, closeModal, id, otaVersionPre, packet } = props;

  //根据版本号前缀查询到的所有车辆
  const [allCars, setAllCars] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });

  // 获取车辆列表数据
  const fetchCarsList = async (page = 1, limit = 10, plateNumber = '', sn = '') => {
    setLoading(true);
    try {
      const params = {
        page,
        limit,
        sort: true,
        plateNumber,
        sn,
        otaVersionPre,
        testGroup: packet === 0//测试组的情况下，传true否则是false
      };
      const res = await firmwareCarsByVersionPrefixRequest(params);
      if (res.data?.code === 0) {
        setAllCars(res.data?.data || []);
        setPagination({
          current: page,
          pageSize: limit,
          total: res.data?.count || 0
        });
      }
    } catch (error) {
      console.log(error);
      message.error('获取车辆列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 初始化加载数据
  useEffect(() => {
    if (otaVersionPre) {
      fetchCarsList();
    }
  }, [otaVersionPre]);

  // 搜索车辆
  const handleSearch = async () => {
    const values = await searchForm.getFieldsValue();
    setSelectedRowKeys([]); // 清空选择
    fetchCarsList(1, pagination.pageSize, values.plateNumber || '', values.sn || '');
  };

  // 重置搜索
  const handleReset = () => {
    searchForm.resetFields();
    setSelectedRowKeys([]); // 清空选择
    fetchCarsList();
  };

  // 分页改变
  const handlePaginationChange = (page: number, pageSize: number) => {
    const values = searchForm.getFieldsValue();
    setSelectedRowKeys([]); // 清空选择
    fetchCarsList(page, pageSize, values.plateNumber || '', values.sn || '');
  };

  // 批量导入选中的车辆SN号
  const handleBatchImport = () => {
    if (selectedRowKeys.length === 0) {
      message.warning('请先选择要导入的车辆');
      return;
    }

    // 获取选中车辆的SN号
    const selectedCars = allCars.filter(car => selectedRowKeys.includes(car.id));
    const selectedSNs = selectedCars.map(car => car.sn).filter(sn => sn); // 过滤掉空值

    if (selectedSNs.length === 0) {
      message.warning('选中的车辆中没有有效的SN号');
      return;
    }

    // 获取当前表单中已有的SN号
    const currentValue = form.getFieldValue('sn') || '';
    const existingSNs = currentValue.split('#').filter((sn: string) => sn.trim()); // 按#分割并过滤空值

    // 统计新增和跳过的数量
    const newSNs = selectedSNs.filter(sn => !existingSNs.includes(sn));
    const skippedCount = selectedSNs.length - newSNs.length;
    const importedCount = newSNs.length;

    // 合并并去重
    const allSNs = [...existingSNs, ...newSNs];
    const uniqueSNs = Array.from(new Set(allSNs)); // 去重

    // 用#连接SN号
    const newValue = uniqueSNs.join('#');

    // 更新表单字段
    form.setFieldsValue({ sn: newValue });

    // 显示详细的导入结果
    if (importedCount > 0 && skippedCount > 0) {
      message.success(`成功导入 ${importedCount} 个车辆SN号，跳过 ${skippedCount} 个重复项`);
    } else if (importedCount > 0) {
      message.success(`成功导入 ${importedCount} 个车辆SN号`);
    } else {
      message.info(`所有选中的 ${skippedCount} 个车辆SN号已存在，未导入新数据`);
    }

    setSelectedRowKeys([]); // 清空选择
  };

  // 提交
  const submit = async () => {
    try {
      const values = await form.validateFields();
      const params = { ...values };
      params.id = id;
      const res = await firmwareBindRequest(params);
      if (res.data?.code === 0) {
        message.success('绑定成功');
        closeModal();
        getFirmwareData();
      }
    } catch (error) {
      console.log(error);
    }
  };

  return (
    <Modal
      title={'绑定车辆'}
      open
      onCancel={closeModal}
      onOk={submit}
      width={'60vw'}
      bodyStyle={{ maxHeight: '70vh', overflow: 'auto' }}
      centered
      maskClosable={false}
    >
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div style={{ flex: 2, marginRight: 16 }}>
          {/* 车辆筛选区域 */}
          <div style={{ marginBottom: 16 }}>

            <Form form={searchForm} layout="inline" autoComplete="off">
              <Row style={{ width: '100%', paddingBottom: 8 }}>
                <Col span={12}>
                  <Form.Item name="plateNumber" label="车牌号">
                    <Input placeholder="请输入车牌号" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="sn" label="SN号">
                    <Input placeholder="请输入SN号" />
                  </Form.Item>
                </Col>
              </Row>
              <Col span={24}>
                <Form.Item>
                  <Button type="primary" onClick={handleSearch}>
                    搜索
                  </Button>
                  <Button style={{ marginLeft: 8 }} onClick={handleReset}>
                    重置
                  </Button>

                </Form.Item>
              </Col>

            </Form>
          </div>

          {/* 车辆列表表格 */}
          <Table
            dataSource={allCars}
            loading={loading}
            pagination={false}
            size="small"
            scroll={{ y: 300 }}
            rowKey="id"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys: any[]) => setSelectedRowKeys(keys),
              type: 'checkbox',
            }}
            columns={[
              {
                title: '车牌号',
                dataIndex: 'plateNumber',
                key: 'plateNumber',
                width: 120,
              },
              {
                title: 'SN号',
                dataIndex: 'sn',
                key: 'sn',
                width: 150,
              },
            ]}
          />

          {/* 分页 */}
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`
            }
            onChange={handlePaginationChange}
            onShowSizeChange={handlePaginationChange}
            style={{ marginTop: 16, textAlign: 'right' }}
          />
        </div>
        <div style={{ alignSelf: 'center' }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
            <Button
              style={{ margin: 8 }}
              onClick={handleBatchImport}
              type="primary"
              icon={<RightOutlined />}
              shape="circle"
              size="large"
            >
            </Button>
            <Button type='link' onClick={handleBatchImport}>  批量添加至右侧</Button>
          </div>

        </div>
        <div style={{ flex: 3, height: '100%' }}>
          <Form form={form} autoComplete="off">
            <Form.Item
              name="sn"
              rules={[{ required: true, message: '请输入车辆SN号' }]}
            >
              <Input.TextArea placeholder="请输入车辆SN号，多个用#隔开，例如：A123#B456#C789" />
            </Form.Item>
          </Form>

        </div>

      </div>

    </Modal>
  );
};

export default BindModal;
